'use client';

import styled from '@emotion/styled';
import { useCallback, useEffect, useState } from 'react';
import { Color } from '@diritto/common/style/color';
import { Typography } from '@diritto/common/style/typography';
import { ButtonSize } from 'constants/button';
import { ModalParams, ModalType } from 'constants/modal';
import { Button } from 'ui-components/common/Button';
import { ModalLayout } from 'ui-components/layout/ModalLayout';
import { useShowModal } from 'ui-hooks/useShowModal';
import { ModalHandler } from 'utils/ModalHandler';
import { isPC } from 'constants/layout';
import { ArrowIcon } from 'icons/ArrowIcon';
import { ExternalLink } from 'ui-components/common/ExternalLink';

export const ContentCoverPolicyAlertModal = () => {
  const { isShow, revealModal, showModal, hideModal } = useShowModal();
  const [state, setState] = useState<ModalParams[ModalType.ContentCoverPolicyAlert]>();

  const handleClose = useCallback(() => {
    state?.onCancel?.();
    hideModal();
  }, [hideModal, state?.onCancel]);

  const handleConfirm = useCallback(() => {
    state?.onConfirm?.();
    hideModal();
  }, [hideModal, state?.onConfirm]);

  useEffect(() => {
    const handleBeforePopState = () => {
      if (isShow) {
        handleClose();
      }
    };

    window.addEventListener('popstate', handleBeforePopState);

    return () => {
      window.removeEventListener('popstate', handleBeforePopState);
    };
  }, [handleClose, isShow]);

  useEffect(() => {
    const listener = (show: boolean, params?: ModalParams[ModalType.ContentCoverPolicyAlert]) => {
      if (show) {
        showModal();
      } else {
        hideModal();
      }
      setState(params);
    };

    ModalHandler.addEventListener(ModalType.ContentCoverPolicyAlert, listener);

    return () => {
      ModalHandler.removeEventListener(ModalType.ContentCoverPolicyAlert);
    };
  }, [hideModal, showModal]);

  return (
    <ModalLayout isShow={isShow} revealModal={revealModal}>
      <StyledModal>
        <div className="flex flex-col items-start justify-start gap-4 mb-6 break-all">
          <div className="flex items-start text-black text-sm font-bold whitespace-pre-line">
            표지 이미지 사용 가이드라인
          </div>

          <div className="flex justify-start text-left text-xxs font-medium">
            <p className="text-gray-600">
              기본 표지 외 이미지를 사용할 경우 <span className="text-neutral-700 font-bold">출처 및 저작권 정보</span>
              를 작품 소개란에 반드시 표기해주세요.
            </p>
          </div>

          <div className="justify-start text-left">
            <span className="text-neutral-700 text-xxs font-bold">가이드 라인 위반 시 :</span>
            <br />
            <div className="flex flex-row items-start text-xxs text-gray-700 font-medium gap-2">
              <div className="flex items-center h-[21px] ml-2">
                <div className="w-[4px] h-[4px] bg-gray-700 rounded-full" />
              </div>
              <p>디리토 내 프로모션 선정에서 불이익이 있을 수 있어요.</p>
            </div>
            <div className="flex flex-row items-start text-xxs text-gray-700 font-medium gap-2">
              <div className="flex items-center h-[21px] ml-2">
                <div className="w-[4px] h-[4px] bg-gray-700 rounded-full" />
              </div>
              <p>운영자 검토 후 기본 표지로 변경되거나 작품이 비공개 될 수 있어요.</p>
            </div>
          </div>

          <div className="justify-start text-left">
            <p className="text-gray-700 text-xxs">권장 규격 : 960*1440 (1:1.5)</p>
          </div>

          <ExternalLink
            href="https://help.diritto.co.kr/hc/ko/articles/13441503391759-작품-표지-가이드라인"
            className="flex flex-row items-center underline decoration-gray-500">
            <p className="text-gray-500 text-xxs">상세 가이드라인 보기</p>
            <ArrowIcon className="w-4 h-4" direction="right" color={Color.Gray500} />
          </ExternalLink>
        </div>
        <StyledButtons>
          <StyledButton
            label={'확인'}
            size={ButtonSize.Small}
            buttonTypography={Typography.XXXSmall}
            onClick={handleConfirm}
          />
        </StyledButtons>
      </StyledModal>
    </ModalLayout>
  );
};

const StyledModal = styled.div`
  width: calc(100vw - 60px);
  max-width: 320px;
  background-color: ${Color.White};
  padding: 24px;
  border-radius: 4px;
  box-shadow:
    0 4px 8px -3px rgba(0, 0, 0, 0.2),
    0px 16px 32px -3px rgba(0, 0, 0, 0.2);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (${isPC}) {
    max-width: 400px;
  }
`;

const StyledButton = styled(Button)`
  padding: 0 20px;
`;

const StyledButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: 40px;
`;
